import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import { subscribeOrders, updateOrderStatus, deleteOrder } from '../../services/db';
import { notifyInternal, notifyCustomer } from '../../services/email';
import { orderStatusEmail } from '../../services/emailTemplates';

const statusColors = {
  Pending: 'warning',
  Processing: 'info',
  Shipped: 'primary',
  Delivered: 'success',
  Cancelled: 'error'
};

function Orders({ searchQuery }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [orders, setOrders] = useState([]);

  // Real-time Firestore listener with localStorage fallback
  useEffect(() => {
    let unsubscribe = () => {};
    try {
      unsubscribe = subscribeOrders((data) => {
        const processed = data.map(o => ({
          ...o,
          total: o.total || o.items.reduce((sum, item) => sum + (item.price * item.quantity), 0),
        }));
        setOrders(processed);
        localStorage.setItem('orders', JSON.stringify(processed));
      });
    } catch (err) {
      console.error('Firestore listener failed — falling back to localStorage:', err);
      const saved = JSON.parse(localStorage.getItem('orders') || '[]');
      setOrders(saved.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
    }
    return () => unsubscribe();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuClick = (event, order) => {
    setAnchorEl(event.currentTarget);
    setSelectedOrder(order);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDetailsOpen = () => {
    setDetailsOpen(true);
    handleMenuClose();
  };

  const handleDetailsClose = () => {
    setDetailsOpen(false);
  };

  const handleStatusChange = async (newStatus) => {
    const order = selectedOrder;
    handleMenuClose();
    if (order) {
      await updateOrderStatus(order._docId, newStatus);
      const orderTotal = order.total ||
        order.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      try {
        const itemLines = order.items.map(i => `  • ${i.name} ×${i.quantity} — $${(i.price * i.quantity).toFixed(2)}`).join('\n');
        const firstName = order.customerName.split(' ')[0];
        await notifyInternal(
          `Order ${order.id} — Status: ${newStatus}`,
          `Customer: ${order.customerName} <${order.email}>\nPhone: ${order.phone || 'N/A'}\n\nStatus: ${newStatus}\n\nItems:\n${itemLines}\n\nTotal: $${orderTotal.toFixed(2)}`,
          order.email,
        );
        await notifyCustomer(
          order.email,
          `Your Order ${order.id} — ${newStatus} | AkdmeTech`,
          orderStatusEmail({
            firstName,
            orderId: order.id,
            status:  newStatus,
            items:   order.items,
            total:   orderTotal,
          }),
        );
      } catch (error) {
        console.error('Failed to send email notification:', error);
      }
    }
  };

  const handleDeleteOrder = async () => {
    const order = selectedOrder;
    handleMenuClose();
    if (order) {
      await deleteOrder(order._docId);
      try {
        await notifyInternal(
          `Order ${order.id} — Cancelled`,
          `Customer: ${order.customerName} <${order.email}>\n\nOrder ID: ${order.id} has been deleted.`,
          order.email,
        );
      } catch (error) {
        console.error('Failed to send email notification:', error);
      }
    }
  };

  const filteredOrders = orders.filter(order => {
    const searchLower = searchQuery.toLowerCase();
    return (
      order.customerName.toLowerCase().includes(searchLower) ||
      order.email.toLowerCase().includes(searchLower) ||
      order.id.toLowerCase().includes(searchLower)
    );
  });

  return (
    <Box>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Order ID</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Total</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredOrders
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((order) => (
                <TableRow key={order.id} hover>
                  <TableCell>{order.id}</TableCell>
                  <TableCell>{order.date}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="body2">{order.customerName}</Typography>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {order.email}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {order.phone}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      ${order.total.toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={order.status}
                      color={statusColors[order.status]}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuClick(e, order)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      
      <TablePagination
        component="div"
        count={filteredOrders.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleDetailsOpen}>View Details</MenuItem>
        <MenuItem onClick={() => handleStatusChange('Processing')}>Mark Processing</MenuItem>
        <MenuItem onClick={() => handleStatusChange('Shipped')}>Mark Shipped</MenuItem>
        <MenuItem onClick={() => handleStatusChange('Delivered')}>Mark Delivered</MenuItem>
        <MenuItem onClick={() => handleStatusChange('Cancelled')}>Cancel Order</MenuItem>
        <MenuItem onClick={handleDeleteOrder} sx={{ color: 'error.main' }}>Delete Order</MenuItem>
      </Menu>

      {selectedOrder && (
        <Dialog
          open={detailsOpen}
          onClose={handleDetailsClose}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              background: 'rgba(45, 52, 54, 0.95)',
              backdropFilter: 'blur(10px)',
              borderRadius: '15px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }
          }}
        >
          <DialogTitle>
            Order Details - {selectedOrder.id}
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Customer Information</Typography>
                <Typography variant="body1">{selectedOrder.customerName}</Typography>
                <Typography variant="body2" color="text.secondary">{selectedOrder.email}</Typography>
                <Typography variant="body2" color="text.secondary">{selectedOrder.phone}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Shipping Address</Typography>
                <Typography variant="body1">{selectedOrder.shippingAddress}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Order Items</Typography>
                <List dense>
                  {selectedOrder.items.map((item, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemText
                        primary={item.name}
                        secondary={`Quantity: ${item.quantity} | Price: $${item.price.toFixed(2)}`}
                      />
                      <Typography variant="body2">
                        ${(item.quantity * item.price).toFixed(2)}
                      </Typography>
                    </ListItem>
                  ))}
                </List>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                  <Typography variant="subtitle1">Total</Typography>
                  <Typography variant="subtitle1">
                    ${selectedOrder.total.toFixed(2)}
                  </Typography>
                </Box>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                <Chip
                  label={selectedOrder.status}
                  color={statusColors[selectedOrder.status]}
                  size="small"
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDetailsClose}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}

export default Orders;
